<template>
  <MarketplaceCard
    v-if="ghl"
    v-model="switchOn"
    :logo-path="GoHighLevelLogo"
    logo-alt="GoHighLevel"
    description="Sync customers, jobs, memberships and appointments to GoHighLevel."
    learn-more-link="https://www.gohighlevel.com/"
    cy-selector="marketplaceGHL"
    :error-text="cardError"
    :notice-text="cardNotice"
  >
    <template #configure>
      <div data-cy="setup-information-link" class="link" @click="openModal">
        Set Up Information
      </div>
    </template>
    <template #modal>
      <Modal
        v-if="modalOpen"
        title="Set Up Information - GoHighLevel"
        :submit-btn-text="
          ghl.api_key_present ? 'Save changes' : 'Connect & save'
        "
        delete-btn-text="Disconnect"
        :saving="saving"
        @submit="saveIntegration"
        @hide-modal="closeModal"
        v-on="disconnectListener"
      >
        <div class="d-flex flex-column gap-3">
          <div data-cy="ghl-connection-header">
            <span
              class="d-inline-flex align-items-center gap-2 rounded-full px-2.5 py-1 text-xs font-medium"
              :class="connectionStatus.pill"
              data-cy="ghl-connection-status"
            >
              <span :class="connectionStatus.dot" />
              {{ connectionStatus.label }}
            </span>
            <p
              v-if="ghl.api_key_present"
              class="mt-2 mb-0 text-sm text-gray-500"
            >
              Location
              <span class="font-medium text-gray-900">{{
                shortLocationId
              }}</span>
              <span class="ml-3">{{ connectionStatus.since }}</span>
              <span class="ml-1 font-medium text-gray-900">
                {{ connectionStatus.sinceValue }}
              </span>
              <span class="ml-3">Queue</span>
              <span class="ml-1 font-medium text-gray-900" data-cy="ghl-queue">
                {{ ghl.outbound_queue_depth ?? 0 }}
              </span>
            </p>
          </div>

          <!-- One bordered container, one row per section. Separate boxes read as
               unrelated cards, and the modal is a single form whose parts happen to be
               collapsible. -->
          <div
            class="rounded-lg border border-gray-200 divide-y divide-gray-200"
          >
            <div data-cy="ghl-connection">
              <button
                type="button"
                class="d-flex align-items-center gap-2 w-100 px-3 py-4 bg-transparent border-0 text-left"
                data-cy="ghl-connection-toggle"
                @click="connectionCollapsed = !connectionCollapsed"
              >
                <i
                  class="fa-solid text-gray-400"
                  :class="
                    connectionCollapsed ? 'fa-chevron-right' : 'fa-chevron-down'
                  "
                />
                <span class="text-sm font-medium text-gray-900">
                  Connection
                </span>
                <span
                  v-if="connectionIssue"
                  :class="connectionIssue.dot"
                  data-cy="ghl-connection-alert"
                />
                <span
                  class="ml-auto text-xs"
                  :class="connectionIssue?.text ?? 'text-gray-500'"
                >
                  {{ connectionSummary }}
                </span>
              </button>
              <div
                v-if="!connectionCollapsed"
                class="d-flex flex-column gap-3 px-3 pt-1 pb-4"
              >
                <div
                  v-if="scopesChecking"
                  data-cy="ghl-scopes-checking"
                  class="d-flex align-items-center gap-2 text-xs text-gray-600"
                >
                  <i class="fa fa-spinner fa-spin" />
                  <span>Checking your token's scopes with GoHighLevel...</span>
                </div>

                <div
                  v-else-if="tokenRejected"
                  data-cy="ghl-token-rejected"
                  class="rounded-lg border border-red-200 bg-red-50 p-3"
                >
                  <div class="d-flex align-items-center gap-2">
                    <i class="fa-solid fa-circle-exclamation text-red-600" />
                    <span class="text-xs font-medium text-gray-900">
                      {{
                        tokenInvalid
                          ? "GoHighLevel does not recognise this token."
                          : "GoHighLevel refused every required scope."
                      }}
                    </span>
                    <button
                      type="button"
                      data-cy="ghl-rejected-scopes-help"
                      class="link ml-auto p-0 text-xs underline bg-transparent border-0"
                      @click="openHelp('token')"
                    >
                      How do I add them?
                    </button>
                  </div>
                  <p
                    v-if="tokenInvalid"
                    class="mt-2 mb-0 text-xs text-gray-700"
                  >
                    It has been rotated, revoked or mistyped, so nothing can
                    sync until it is replaced. Private Integration Tokens cannot
                    be refreshed, create a new one in
                    <strong>Settings, then Private Integrations</strong> and
                    paste it in <strong>Private Integration Token</strong>.
                  </p>
                  <p v-else class="mt-2 mb-0 text-xs text-gray-700">
                    The token is recognised but no scope is selected. Open
                    <strong>Settings, then Private Integrations</strong> in
                    GoHighLevel, select every required scope, then save.
                  </p>
                </div>

                <div
                  v-else-if="hasMissingScopes"
                  data-cy="ghl-missing-scopes"
                  class="rounded-lg border border-amber-200 bg-amber-50 p-3"
                >
                  <div class="d-flex align-items-center gap-2">
                    <span class="ghl-status-dot--warn" />
                    <span class="text-xs font-medium text-gray-900">
                      Your token is missing {{ missingScopes.length }} required
                      {{ missingScopes.length === 1 ? "scope" : "scopes" }}.
                    </span>
                    <button
                      type="button"
                      data-cy="ghl-missing-scopes-help"
                      class="link ml-auto p-0 text-xs underline bg-transparent border-0"
                      @click="openHelp('token')"
                    >
                      How do I add them?
                    </button>
                  </div>
                  <p class="mt-2 mb-1 text-xs text-gray-700">
                    <span data-cy="ghl-sync-paused">
                      Syncing is paused, so nothing reaches GoHighLevel until
                      these are selected.
                    </span>
                    All {{ scopeStatuses.length }} scopes below are required.
                    Select every one that is not already selected in
                    GoHighLevel, then save your token again to resume.
                  </p>
                  <ul class="ml-0 mb-0 list-none text-xs">
                    <li
                      v-for="scope in scopeStatuses"
                      :key="scope.name"
                      class="d-flex align-items-center gap-1"
                    >
                      <i :class="scopeIcon(scope.status)" />
                      <code>{{ scope.name }}</code>
                    </li>
                  </ul>
                </div>

                <div
                  v-else-if="scopeCheckUnknown"
                  data-cy="ghl-scopes-unknown"
                  class="d-flex align-items-center gap-2 text-xs text-gray-600"
                >
                  <i class="fa-regular fa-circle-question text-gray-500" />
                  <span>
                    Could not reach GoHighLevel to check your token's scopes.
                  </span>
                </div>

                <!-- Pre-connect only. Both are things to do in GoHighLevel before the first
                   push lands, and neither is actionable once records are already flowing. -->
                <template v-if="!ghl.api_key_present">
                  <Alert
                    type="info"
                    class="ghl-callout"
                    data-cy="ghl-dedup-notice"
                  >
                    <span class="d-block text-sm font-medium text-gray-900">
                      Before you connect, check your dedup settings
                    </span>
                    <span class="d-block mt-1 text-xs text-gray-700">
                      Sera allows duplicate phone numbers between customers,
                      GoHighLevel does not by default. Set your GoHighLevel
                      dedup to
                      <em>Email only</em> for the smoothest sync.
                    </span>
                    <button
                      type="button"
                      data-cy="ghl-dedup-help"
                      class="link d-block mt-1 p-0 text-xs bg-transparent border-0"
                      @click="openHelp('dedup')"
                    >
                      Where to find it in GoHighLevel
                    </button>
                  </Alert>

                  <!-- Gated on the checkbox: nothing existing is pushed when it is off, so
                     there is no wave of automations to pause. -->
                  <Alert
                    v-if="runBackfill"
                    type="warning"
                    class="ghl-callout"
                    data-cy="ghl-automation-warning"
                  >
                    <span class="d-block text-sm font-medium text-gray-900">
                      Pause your GoHighLevel automations that fire on new
                      contacts
                    </span>
                    <span class="d-block mt-1 text-xs text-gray-700">
                      Any workflow that runs when a contact is created (welcome
                      messages, review requests, campaign triggers) will fire
                      once for every existing record we push. If you do not want
                      that, pause those workflows in GoHighLevel before you
                      connect.
                    </span>
                  </Alert>
                </template>

                <!-- Own label row rather than InputField's title, so the guide link can sit
                   opposite it. The steps are long enough to bury the field they describe,
                   which is why they live in a drawer instead of under it. -->
                <div>
                  <div
                    class="d-flex align-items-center justify-content-between mb-1"
                  >
                    <span class="text-sm font-medium text-gray-900">
                      Private Integration Token
                    </span>
                    <button
                      type="button"
                      data-cy="ghl-token-help"
                      class="link p-0 text-xs bg-transparent border-0"
                      @click="openHelp('token')"
                    >
                      How do I get this?
                    </button>
                  </div>
                  <InputField
                    v-model="apiKey"
                    data-cy="ghl-api-key"
                    type="password"
                    toggleable
                    dense
                    :disabled="tokenLocked"
                    :placeholder="
                      ghl.api_key_present
                        ? 'Token saved. Enter a new one to replace it.'
                        : 'Paste your token'
                    "
                  />
                </div>
                <!-- A saved token is the usual state and is never edited by accident.
                   Unlocking is deliberate, so replacing one is a decision rather than a
                   field someone tabs into. Checkbox's own label carries ms-2, which sets it
                   in from the field above; -ml-2 puts them back on one left edge. -->
                <Checkbox
                  v-if="ghl.api_key_present"
                  v-model="replacingToken"
                  label="Replace the saved token"
                  data-cy="ghl-replace-token"
                  class="-mt-2 -ml-2"
                />

                <div>
                  <div
                    class="d-flex align-items-center justify-content-between mb-1"
                  >
                    <span class="text-sm font-medium text-gray-900"
                      >Location ID</span
                    >
                    <button
                      type="button"
                      data-cy="ghl-location-help"
                      class="link p-0 text-xs bg-transparent border-0"
                      @click="openHelp('location')"
                    >
                      How do I get this?
                    </button>
                  </div>
                  <InputField
                    v-model="locationId"
                    data-cy="ghl-location-id"
                    placeholder="Enter GHL location id"
                    dense
                  />
                </div>

                <!-- First connect only. Afterwards the sweep has either run or is a decision
                   made, and offering it again would read as a way to re-push everything. -->
                <template v-if="!ghl.api_key_present">
                  <hr class="my-1 border-gray-200" />
                  <Checkbox
                    v-model="runBackfill"
                    label="Sync my existing records on connect"
                    helper-text="Pushes your existing customers, service addresses, memberships, jobs and appointments into GoHighLevel. Uncheck to sync only new activity going forward."
                    data-cy="ghl-run-backfill"
                    class="-ml-2"
                  />
                  <!-- Only when something existing is actually being pushed: with the box
                     off there is no history to cap. -->
                  <div v-if="runBackfill" data-cy="ghl-backfill-window">
                    <span
                      class="d-block mb-2 text-sm font-medium text-gray-900"
                    >
                      How far back should we sync jobs and appointments?
                    </span>
                    <ButtonGroup
                      class="ghl-backfill-window-picker"
                      :buttons="backfillWindowButtons"
                      @update:buttons="pickBackfillWindow"
                    />
                    <p class="mt-2 mb-0 text-xs text-gray-600">
                      Contacts, service addresses and memberships always sync in
                      full, this cap applies to jobs and appointments only.
                      <strong>90 days is enough for most tenants.</strong>
                      Longer windows can take several hours and use more of your
                      GoHighLevel rate limit. Pick 1 year only if you need
                      historical opportunity value in your pipeline on day one.
                    </p>
                  </div>
                </template>
                <p
                  v-if="ghl.api_key_present && needsReauth"
                  class="mb-0 text-xs text-yellow-700"
                >
                  GoHighLevel rejected recent requests. Enter a new Private
                  Integration Token and save to reconnect.
                </p>

                <!-- Moved in from the old connected block: it is a setting on the
                   connection, and it gates whether a sweep may run at all. -->
                <!-- No box of its own: it is one line in the Connection section, not a
                     card inside a card. Shown before a token exists too, because it is
                     something to set up in GoHighLevel before connecting. -->
                <div class="mt-1">
                  <div class="d-flex align-items-center gap-2">
                    <span :class="duplicateDotClass" />
                    <span class="font-medium text-gray-900">
                      Multiple opportunities per contact
                    </span>
                    <span class="text-xs" :class="duplicateTextClass">
                      {{ duplicateStatusLabel }}
                    </span>
                    <button
                      type="button"
                      data-cy="ghl-duplicate-help"
                      class="link ml-auto p-0 text-xs bg-transparent border-0"
                      @click="openHelp('duplicate')"
                    >
                      How do I turn this on?
                    </button>
                  </div>
                  <!-- Only when GoHighLevel actually told us the setting is off. An
                       answer we could not get is not an answer of "off". -->
                  <p
                    v-if="
                      ghl.api_key_present &&
                      duplicateOpportunitiesState === 'unknown'
                    "
                    class="mt-1 mb-0 text-xs text-gray-600"
                  >
                    GoHighLevel did not answer, so this could not be read. It is
                    usually the daily request limit, which resets on its own.
                  </p>
                  <!-- Offered for both an unread setting and one that is off, and only
                       once there is a token to read it with. -->
                  <button
                    v-if="
                      ghl.api_key_present &&
                      !tokenRejected &&
                      duplicateOpportunitiesState !== 'enabled'
                    "
                    type="button"
                    data-cy="ghl-dup-recheck"
                    class="inline-flex items-center gap-2 mt-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 !rounded-lg hover:bg-gray-100 disabled:opacity-60 disabled:cursor-not-allowed"
                    :disabled="duplicateChecking"
                    title="Re-read the setting from GoHighLevel"
                    @click="refreshDuplicateSetting"
                  >
                    <i
                      class="fa-regular fa-arrows-rotate"
                      :class="{ 'fa-spin': duplicateChecking }"
                    />
                    {{ duplicateChecking ? "Re-checking..." : "Re-check" }}
                  </button>
                </div>
              </div>
            </div>

            <!-- All of this is read through the token, so a rejected one makes every
               line of it stale: a green "Connected", a last-sync time and failures
               from before the token died. Replaced by the panel above. -->
            <!-- Its own section: a sweep runs for hours and is the thing an admin
                 reopens the window to check, so it should not sit behind the form. -->
            <div
              v-if="ghl.api_key_present && !tokenRejected"
              data-cy="ghl-backfill"
            >
              <button
                type="button"
                class="d-flex align-items-center gap-2 w-100 px-3 py-4 bg-transparent border-0 text-left"
                data-cy="ghl-sync-toggle"
                @click="syncCollapsed = !syncCollapsed"
              >
                <i
                  class="fa-solid text-gray-400"
                  :class="
                    syncCollapsed ? 'fa-chevron-right' : 'fa-chevron-down'
                  "
                />
                <span class="text-sm font-medium text-gray-900">
                  Sync progress
                </span>
                <span
                  class="ml-auto text-xs"
                  :class="
                    syncBadge.pill
                      ? `rounded-full px-2 py-0.5 font-semibold ${syncBadge.pill}`
                      : 'text-gray-500'
                  "
                  data-cy="ghl-sync-badge"
                >
                  {{ syncBadge.label }}
                </span>
              </button>
              <div v-if="!syncCollapsed" class="px-3 pt-1 pb-4">
                <p class="mb-2 text-xs text-gray-700">{{ backfillDetail }}</p>

                <!-- One row per step. A single aggregate bar said how far the run was
                     overall and nothing about which step was moving, which is the only
                     thing that changes over the hours a large sweep takes. -->
                <ul class="ghl-phase-list mb-2" data-cy="ghl-backfill-progress">
                  <li v-for="row in backfillPhases" :key="row.key" class="py-2">
                    <div class="d-flex align-items-center gap-2">
                      <i :class="phaseIcon(row)" />
                      <span
                        class="text-sm"
                        :class="
                          row.state === 'waiting'
                            ? 'text-gray-400'
                            : 'text-gray-900'
                        "
                      >
                        {{ row.label }}
                      </span>
                      <span
                        v-if="row.capped"
                        class="rounded bg-yellow-50 px-1.5 py-0.5 text-xs font-medium text-yellow-700"
                        title="Capped by the window chosen on connect"
                      >
                        {{ backfillWindowBadge }}
                      </span>
                    </div>
                    <div
                      class="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-gray-200"
                      role="progressbar"
                      :aria-valuenow="phasePercent(row)"
                      aria-valuemin="0"
                      aria-valuemax="100"
                    >
                      <div
                        class="h-1.5 rounded-full transition-all"
                        :class="phaseBar(row)"
                        :style="{ width: `${phasePercent(row)}%` }"
                      />
                    </div>
                    <p class="mt-1 mb-0 text-right text-xs">
                      <span
                        v-if="row.state === 'waiting' && !row.total"
                        class="text-gray-400"
                      >
                        Waiting
                      </span>
                      <template v-else>
                        <span
                          v-if="phaseState(row) === 'partial'"
                          class="mr-1 text-yellow-600"
                        >
                          {{ (row.total - row.done).toLocaleString() }} not
                          accepted
                        </span>
                        <span class="font-semibold text-gray-900">
                          {{ row.done.toLocaleString() }}
                        </span>
                        <span class="text-gray-500">
                          / {{ row.total.toLocaleString() }}
                        </span>
                      </template>
                    </p>
                  </li>
                </ul>

                <!-- Only before the first run. The window is chosen here because an
                     admin who declined the sweep on connect never saw that picker; once
                     a run has started the choice is stored and a resume or retry keeps
                     it, so offering it again would invite changing the cap underneath a
                     sweep that is already counted against it. -->
                <div
                  v-if="backfillControl && backfillControl.action === 'start'"
                  class="mb-2"
                  data-cy="ghl-start-window"
                >
                  <span class="d-block mb-1 text-xs text-gray-600">
                    How far back should we sync jobs and appointments?
                  </span>
                  <ButtonGroup
                    class="ghl-backfill-window-picker"
                    :buttons="backfillWindowButtons"
                    @update:buttons="pickBackfillWindow"
                  />
                </div>

                <!-- One control at most. A run in flight can be paused, one the admin
                     paused can be resumed, one that broke can be retried, and a run
                     paused on quota gets nothing because it resumes by itself. -->
                <button
                  v-if="backfillControl"
                  type="button"
                  :data-cy="`ghl-backfill-${backfillControl.action}`"
                  class="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 !rounded-lg hover:bg-gray-100 disabled:opacity-60 disabled:cursor-not-allowed"
                  :disabled="backfillBusy"
                  :title="backfillControl.title"
                  @click="runBackfillControl"
                >
                  <i
                    :class="[backfillControl.icon, { 'fa-spin': backfillBusy }]"
                  />
                  {{
                    backfillBusy ? backfillControl.busy : backfillControl.label
                  }}
                </button>

                <Alert
                  v-if="backfill.state === 'running'"
                  type="warning"
                  class="ghl-callout mt-2"
                  data-cy="ghl-backfill-note"
                >
                  <span class="d-block text-sm font-medium text-gray-900">
                    You do not need to wait for this to finish
                  </span>
                  <span class="d-block mt-1 text-xs text-gray-700">
                    New records you create in Sera sync straight away even while
                    the backfill is running. Close this window and keep working,
                    anything that goes wrong is listed under Recent issues.
                  </span>
                </Alert>
              </div>
            </div>

            <div
              v-if="ghl.api_key_present && !tokenRejected"
              data-cy="ghl-issues"
            >
              <button
                type="button"
                class="d-flex align-items-center gap-2 w-100 px-3 py-4 bg-transparent border-0 text-left"
                data-cy="ghl-issues-toggle"
                @click="issuesCollapsed = !issuesCollapsed"
              >
                <i
                  class="fa-solid text-gray-400"
                  :class="
                    issuesCollapsed ? 'fa-chevron-right' : 'fa-chevron-down'
                  "
                />
                <span class="text-sm font-medium text-gray-900">
                  Recent issues
                </span>
                <span
                  v-if="failureCount"
                  class="ml-auto rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700"
                >
                  {{ failureCount }}
                </span>
                <!-- Says the period, not just "none": the count only ever covers the
                     window picked below, and "None" read as "nothing has ever failed". -->
                <span
                  v-else
                  class="ml-auto rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700"
                >
                  0 in last {{ windowLabel(failureWindow) }}
                </span>
              </button>
              <div v-if="!issuesCollapsed" class="px-3 pt-1 pb-4">
                <div>
                  <div class="d-flex align-items-center mb-2">
                    <ButtonGroup
                      data-cy="ghl-failure-window"
                      class="ghl-window-picker"
                      :buttons="windowButtons"
                      @update:buttons="pickWindow"
                    />
                  </div>

                  <p
                    v-if="!failureGroups.length"
                    class="py-4 text-center text-sm text-gray-500"
                    data-cy="ghl-no-failures"
                  >
                    No sync issues in the {{ windowProse(failureWindow) }}. If
                    something changes, failures are grouped here with a link to
                    the records they affected.
                  </p>

                  <ul
                    v-else
                    class="ghl-issue-groups space-y-2 text-sm"
                    data-cy="ghl-failure-groups"
                  >
                    <li
                      v-for="group in failureGroups"
                      :key="group.key"
                      class="overflow-hidden rounded-lg border border-gray-200"
                    >
                      <button
                        type="button"
                        class="d-flex align-items-center gap-2 w-100 px-3 py-2 bg-gray-100 border-0 text-left"
                        :data-cy="`ghl-failure-group-${group.key}`"
                        @click="toggleGroup(group.key)"
                      >
                        <i
                          class="fa-solid text-gray-400"
                          :class="
                            openGroups[group.key]
                              ? 'fa-chevron-down'
                              : 'fa-chevron-right'
                          "
                        />
                        <!-- The reason heads the group, not the endpoint. A path an
                             admin has no way to act on used to be the most prominent
                             thing on the row. -->
                        <span
                          class="font-medium text-gray-900 whitespace-nowrap"
                        >
                          {{ group.reason }}
                        </span>
                        <span
                          v-if="group.hint"
                          class="min-w-0 text-xs text-gray-500 truncate"
                        >
                          · {{ group.hint }}
                        </span>
                        <span
                          class="ml-auto flex-shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold"
                          :class="groupBadgeClass(group)"
                          :title="`${group.count} in this period`"
                        >
                          {{ group.count }}
                        </span>
                      </button>

                      <div
                        v-if="openGroups[group.key]"
                        class="bg-white px-3 pt-2 pb-3"
                      >
                        <p
                          v-if="group.advice"
                          class="mb-2 text-xs text-gray-700"
                        >
                          {{ group.advice }}
                        </p>

                        <!-- Fixed height with its own scroll: revealing more rows must
                             not push everything below it down the modal. -->
                        <ul
                          :ref="(el) => (rowLists[group.key] = el)"
                          class="ghl-issue-rows mb-0"
                        >
                          <!-- Three columns rather than a flex row: the kind, the id
                               and the name each line up down the list, which is what
                               makes eighteen of them scannable. -->
                          <li
                            v-for="failure in shownFailures(group)"
                            :key="failure.id"
                            class="py-1.5 text-xs"
                            :class="
                              groupHasNames(group)
                                ? 'ghl-issue-row'
                                : 'ghl-issue-row--bare'
                            "
                          >
                            <span class="text-gray-500">
                              {{ recordLabel(failure) }}
                            </span>
                            <span class="text-right truncate">
                              <a
                                v-if="failure.contact_url"
                                class="link font-mono"
                                :href="failure.contact_url"
                                target="_blank"
                                rel="noopener"
                              >
                                {{ failure.contact_id }}
                              </a>
                              <span v-else class="font-mono text-gray-500">
                                {{ failure.contact_id || "no id" }}
                              </span>
                            </span>
                            <!-- Rendered only when the group reserves a column for it:
                                 an empty span would still create a grid track and hold
                                 the ids away from the edge. -->
                            <span
                              v-if="groupHasNames(group)"
                              class="text-right text-gray-900 truncate"
                            >
                              <template v-if="failure.contact_name">
                                · {{ failure.contact_name }}
                              </template>
                            </span>
                          </li>
                        </ul>

                        <button
                          v-if="hiddenCount(group)"
                          type="button"
                          class="d-block w-100 mt-2 py-1 text-center text-xs text-gray-500 bg-transparent border-0"
                          :data-cy="`ghl-failure-more-${group.key}`"
                          @click="revealMore(group)"
                        >
                          + {{ hiddenCount(group) }} more
                        </button>
                        <p
                          v-else-if="group.count > group.failures.length"
                          class="mt-2 mb-0 text-xs text-gray-400"
                        >
                          Showing the most recent
                          {{ group.failures.length }} of {{ group.count }}.
                        </p>

                        <p class="mt-2 mb-0 text-xs text-gray-400">
                          Reference HTTP {{ group.response_code }}
                        </p>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- Outside the connected block: the axes decide what the very first push
               carries, so they have to be settable before that push happens. -->
            <!-- A plain disclosure rather than CollapsibleCard. Its header lays the
               toggle out after the title with align-items: baseline, and its rules are
               scoped, so pulling the chevron to the left and onto the centre line took
               an !important for every property. Owning the header is less markup than
               fighting it. -->
            <div data-cy="ghl-tags">
              <button
                type="button"
                class="d-flex align-items-center gap-2 w-100 px-3 py-4 bg-transparent border-0 text-left"
                data-cy="ghl-tags-toggle"
                @click="tagsCollapsed = !tagsCollapsed"
              >
                <i
                  class="fa-solid text-gray-400"
                  :class="
                    tagsCollapsed ? 'fa-chevron-right' : 'fa-chevron-down'
                  "
                />
                <span class="text-sm font-medium text-gray-900"
                  >Contact tags</span
                >
                <span class="ml-auto text-xs text-gray-500">
                  {{ enabledTagCount }} of {{ TAG_AXES.length }} on
                </span>
              </button>
              <div v-if="!tagsCollapsed" class="px-3 pt-1 pb-4">
                <p class="mb-2 text-xs text-gray-600">
                  Tags are attached to the GoHighLevel contact whenever a
                  customer syncs. Turn any option off to stop sending it.
                </p>
                <template v-for="group in TAG_GROUPS" :key="group.title">
                  <p class="ghl-guide-heading mt-4">{{ group.title }}</p>
                  <div v-for="axis in group.axes" :key="axis.key" class="mt-2">
                    <Checkbox
                      v-model="tagSettings[axis.key]"
                      :label="axis.label"
                      :data-cy="`ghl-tag-${axis.key}`"
                      class="-ml-2"
                    />
                    <p class="mt-1 mb-0 ml-4 text-xs text-gray-500">
                      {{ axis.hint }}
                    </p>
                  </div>
                </template>
              </div>
            </div>

            <div
              v-if="ghl.api_key_present && !tokenRejected"
              data-cy="ghl-settings"
            >
              <button
                type="button"
                class="d-flex align-items-center gap-2 w-100 px-3 py-4 bg-transparent border-0 text-left"
                data-cy="ghl-settings-toggle"
                @click="settingsCollapsed = !settingsCollapsed"
              >
                <i
                  class="fa-solid text-gray-400"
                  :class="
                    settingsCollapsed ? 'fa-chevron-right' : 'fa-chevron-down'
                  "
                />
                <span class="text-sm font-medium text-gray-900">Settings</span>
              </button>
              <div v-if="!settingsCollapsed" class="px-3 pt-1 pb-4">
                <div
                  class="d-flex align-items-start justify-content-between gap-3 py-2"
                >
                  <div>
                    <span class="d-block text-sm font-medium text-gray-900">
                      Push updates in real time
                    </span>
                    <span class="d-block text-xs text-gray-500">
                      Sera edits reach GoHighLevel within a few seconds. Turn
                      off to pause syncing without disconnecting.
                    </span>
                  </div>
                  <Toggle
                    data-cy="ghl-toggle-realtime"
                    :checked="realtimePush"
                    :disabled="savingSetting"
                    @update:checked="setRealtimePush"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Teleported out of the modal, and wrapped in its own stacking context.
               .modal-dialog is transformed, which makes it the containing block for
               anything fixed inside it, so a drawer left in place anchored to the
               modal rather than the viewport. The wrapper's z-index is what then
               keeps it above the modal it opened from. -->
          <Teleport to="body">
            <div class="ghl-help-drawer relative z-[1040]">
              <Drawer
                v-if="helpTopic"
                :title="helpTitle"
                :aria-label="helpTitle"
                no-footer
                data-cy="ghl-help-drawer"
                @hide-drawer="closeHelp"
              >
                <!-- Drawer's default title sits in a tall header with its own margin,
                     which left the guide opening on an empty band of white. -->
                <template #title>
                  <h2
                    class="mb-0 pr-10 text-base font-semibold text-gray-900"
                    data-cy="ghl-help-title"
                  >
                    {{ helpTitle }}
                  </h2>
                </template>
                <div class="ghl-guide-body px-4 pt-2 pb-4 text-gray-700">
                  <p class="ghl-guide-heading">Where it lives in GoHighLevel</p>

                  <template v-if="helpTopic === 'token'">
                    <ol class="ghl-guide-list ghl-guide-list--number space-y-1">
                      <li>Log into your GoHighLevel sub-account.</li>
                      <li>
                        Open <strong>Settings</strong>, then
                        <strong>Private Integrations</strong>.
                      </li>
                      <li>
                        Choose <strong>Create new integration</strong> and name
                        it something you will recognise later, such as Sera.
                      </li>
                      <li>
                        Select every scope in the list below. Miss one and the
                        matching part of the sync will fail.
                      </li>
                      <li>
                        Create the integration, copy the token it shows you, and
                        paste it into the token field.
                        <strong>GoHighLevel only shows the token once.</strong>
                      </li>
                    </ol>
                    <p class="ghl-guide-heading mt-4">Scopes to select</p>
                    <ul
                      class="ghl-guide-list ghl-guide-list--bullet ghl-scope-grid"
                    >
                      <li v-for="scope in requiredScopes" :key="scope">
                        <code>{{ scope }}</code>
                      </li>
                    </ul>
                  </template>

                  <template v-else-if="helpTopic === 'location'">
                    <p>
                      Your Location ID lives in GoHighLevel under
                      <strong>Settings</strong>, then
                      <strong>Business Profile</strong>. You can copy it
                      directly from there.
                    </p>
                    <p class="mt-3 mb-0">
                      It is also visible in the browser address bar while you
                      are in your sub-account, right after
                      <code>/location/</code>.
                    </p>
                  </template>

                  <template v-else-if="helpTopic === 'duplicate'">
                    <ol class="ghl-guide-list ghl-guide-list--number space-y-1">
                      <li>
                        In GoHighLevel, open <strong>Settings</strong>, then
                        <strong>Objects</strong>, then
                        <strong>Opportunities</strong>.
                      </li>
                      <li>
                        Enable
                        <strong>
                          "Allow more than one opportunity per contact"
                        </strong>
                        and save.
                      </li>
                      <li>Return here and press <strong>Re-check</strong>.</li>
                    </ol>
                    <p class="ghl-guide-heading mt-4">Why this matters</p>
                    <p class="mb-0">
                      GoHighLevel keeps one opportunity per contact by default,
                      so a repeat customer's later jobs have nowhere to land and
                      are refused. The first bulk sync is held back until this
                      is on, because it would otherwise push a whole history and
                      silently drop every job after each customer's first.
                    </p>
                  </template>

                  <template v-else>
                    <ol class="ghl-guide-list ghl-guide-list--number space-y-1">
                      <li>
                        Open <strong>Settings</strong>, then
                        <strong>Business Profile</strong>.
                      </li>
                      <li>
                        Scroll to
                        <strong>Contact Deduplication Preferences</strong>.
                      </li>
                      <li>
                        Set <strong>Find existing contacts based on</strong> to
                        <em>Email</em>.
                      </li>
                      <li>
                        Set <strong>Second preference for search</strong> to
                        <em>None</em>.
                      </li>
                    </ol>
                    <p class="ghl-guide-heading mt-4">Why this matters</p>
                    <p class="mb-0">
                      Sera allows two customers to share a phone number
                      (spouses, roommates, business contacts). GoHighLevel's
                      default treats matching phone numbers as duplicates and
                      blocks the sync. Setting dedup to
                      <em>Email only</em> matches Sera's model and removes the
                      confusion.
                    </p>
                  </template>
                </div>
              </Drawer>
            </div>
          </Teleport>
        </div>
      </Modal>
      <Modal
        v-if="deleteConfirm"
        title="Disconnect GoHighLevel"
        @hide-modal="toggleDeleteConfirm"
      >
        <div>
          This disconnects GoHighLevel and stops syncing your records:
          customers, jobs, memberships, appointments and their service
          addresses. Are you sure?
        </div>
        <template #submit>
          <Button id="submit_modal_btn" type="danger" @click="dropIntegration">
            Disconnect
          </Button>
        </template>
      </Modal>
    </template>
  </MarketplaceCard>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { useStore } from "vuex";

import Button from "@/components/Button.vue";
import Modal from "@/components/Modal.vue";
import Alert from "@/components/Seranova/Alert/Alert.vue";
import ButtonGroup from "@/components/Seranova/ButtonGroups/ButtonGroup.vue";
import Checkbox from "@/components/Seranova/Checkbox/Checkbox.vue";
import Drawer from "@/components/Seranova/Drawer/Drawer.vue";
import InputField from "@/components/Seranova/InputField/InputField.vue";
import Toggle from "@/components/Seranova/Toggle/Toggle.vue";
import GoHighLevelLogo from "@/images/gohighlevel_logo.png";
import MarketplaceCard from "./MarketplaceCard.vue";

const store = useStore();

const switchOn = ref<boolean | null>(null);
const modalOpen = ref(false);
const deleteConfirm = ref(false);
const saving = ref(false);
const apiKey = ref("");
const locationId = ref("");
// A stored token is locked until the admin says they are replacing it.
const replacingToken = ref(false);
// Selected by default: a tenant who connects and then finds GoHighLevel empty has no
// obvious way back, so the sweep is the expected outcome. The amber note beside it is
// what makes the cost of it visible before they save.
const runBackfill = ref(true);

// Mirrors Integration::GHL::Config::BACKFILL_WINDOWS. Caps jobs and appointments only,
// which are the two phases a tenant's history makes unbounded.
const BACKFILL_WINDOWS = [
  { key: "90d", label: "90 days (recommended)" },
  { key: "6m", label: "6 months" },
  { key: "1y", label: "1 year" }
] as const;
const DEFAULT_BACKFILL_WINDOW = "90d";
const backfillWindow = ref<string>(DEFAULT_BACKFILL_WINDOW);

// Which of the three guides the side panel is showing, or null for closed.
type HelpTopic = "token" | "location" | "dedup" | "duplicate";
const HELP_TITLES: Record<HelpTopic, string> = {
  token: "How to get your Private Integration Token",
  location: "How to get your Location ID",
  dedup: "Configuring dedup in GoHighLevel",
  duplicate: "Allowing more than one opportunity per contact"
};
const helpTopic = ref<HelpTopic | null>(null);
const helpTitle = computed(() =>
  helpTopic.value ? HELP_TITLES[helpTopic.value] : ""
);

function openHelp(topic: HelpTopic) {
  helpTopic.value = topic;
}

function closeHelp() {
  helpTopic.value = null;
}

// Mirrors Integration::GHL::Config::TAG_AXES.
const TAG_AXES = [
  {
    key: "lead_source",
    label: "Lead source",
    hint: "e.g. lead-source-yelp, from the customer's first job"
  },
  {
    key: "service_category",
    label: "Service category",
    hint: "e.g. service-hvac, deduped across recent appointments"
  },
  {
    key: "member_status",
    label: "Member status",
    hint: "member-current or member-none, refreshed on status change"
  },
  {
    key: "equipment_age",
    label: "Equipment age",
    hint: "e.g. equipment-age-10-15, bucketed from the oldest component"
  },
  {
    key: "custom_tags",
    label: "Customer Tags from Sera Admin",
    hint: "Passes the tags you have assigned in Sera's Tag Manager (e.g. do-not-service, canceled-agreement, permit-needed) through to the GoHighLevel contact as custom-{tag}."
  }
] as const;

// The last axis derives nothing, it forwards what the tenant already set in Sera, so
// it reads under its own heading rather than alongside the four that are computed.
const TAG_GROUPS = [
  { title: "Automatic tag axes", axes: TAG_AXES.slice(0, 4) },
  { title: "From Sera Admin", axes: TAG_AXES.slice(4) }
] as const;

// Collapsed by default: the axes all default to on, so this is a section to open
// only when changing one.
const tagsCollapsed = ref(true);

// Open by default: it is the section that has to be filled in before anything works.
const connectionCollapsed = ref(false);
// Both start closed. Selecting the sync checkbox on connect opens the progress one and
// closes Connection, so the admin lands on the thing that is actually happening.
const syncCollapsed = ref(true);
const issuesCollapsed = ref(true);
const settingsCollapsed = ref(true);

const savingSetting = ref(false);
const realtimePush = ref(true);

// Reverted on the next tick, not this one. Flipping the value back synchronously leaves
// it unchanged across the render, so Vue patches nothing and the browser's own checkbox
// stays where the click put it.
async function setRealtimePush(enabled) {
  savingSetting.value = true;
  realtimePush.value = enabled;
  try {
    await store.dispatch("changeGhl", { realtime_push: enabled });
    window.notify.success(
      enabled ? "Real-time syncing on." : "Real-time syncing paused."
    );
  } catch {
    window.notify.error("Could not save that setting.");
    nextTick(() => {
      realtimePush.value = !enabled;
    });
  }
  savingSetting.value = false;
}

const tagSettings = ref<Record<string, boolean>>(
  Object.fromEntries(TAG_AXES.map((axis) => [axis.key, true]))
);

// The pill under the modal title, plus the line beneath it. One sentence for the state
// the whole integration is in, readable before any section is opened.
const GREY = "bg-gray-100 text-gray-600";

function pill(label, dot, tone, since, sinceValue) {
  return { label, dot, pill: tone, since, sinceValue };
}

const connectionStatus = computed(() => {
  if (!ghl.value?.api_key_present) {
    return pill("Not Connected", "ghl-status-dot--unknown", GREY, "", "");
  }

  const since = backfillRunning.value ? "Started" : "Last sync";
  const value = backfillRunning.value
    ? formatTimestamp(backfill.value.started_at) || lastSyncLabel.value
    : lastSyncLabel.value;

  if (tokenRejected.value || needsReauth.value) {
    return pill(
      "Reconnect Needed",
      "ghl-status-dot--warn",
      "bg-yellow-50 text-yellow-600",
      since,
      value
    );
  }
  if (!ghl.value?.enabled) {
    return pill("Turned Off", "ghl-status-dot--unknown", GREY, since, value);
  }
  // Ahead of both of the below: a tenant missing a scope, or with duplicate
  // opportunities off, is refused at the gate, so nothing is running and any issue
  // count is from before. The one thing worth saying is that nothing is moving.
  if (connectionIssue.value) {
    return pill(
      `Connected · ${connectionIssue.value.short}`,
      connectionIssue.value.dot,
      connectionIssue.value.pill,
      since,
      value
    );
  }
  if (backfillRunning.value) {
    return pill(
      "Backfilling Existing Records",
      "ghl-status-dot--busy",
      "bg-blue-50 text-blue-700",
      since,
      value
    );
  }
  if (failureCount.value) {
    return pill(
      `Connected · ${failureCount.value} ${failureCount.value === 1 ? "Issue" : "Issues"} To Review`,
      "ghl-status-dot--warn",
      "bg-yellow-50 text-yellow-600",
      since,
      value
    );
  }
  return pill(
    "Connected · Syncing",
    "ghl-status-dot",
    "bg-green-50 text-green-800",
    since,
    value
  );
});

const backfillRunning = computed(() => backfill.value.state === "running");

// Long enough to identify, short enough to sit on one line beside the timestamp.
const shortLocationId = computed(() => {
  const id = ghl.value?.location_id ?? "";
  return id.length > 12 ? `${id.slice(0, 5)}...${id.slice(-4)}` : id;
});

// What is standing between this tenant and a working sync, if anything. Two severities,
// because they are not the same problem: a missing scope means that part of the sync
// cannot work at all, where the duplicate-opportunity setting silently drops a repeat
// customer's later jobs. Both are fixed in GoHighLevel and both are reported here.
// The window's own probe is not the only thing that knows. A sweep refused at the gate
// records the same finding on the task, and it reaches the window first, so reading only
// the probe left the header claiming the sync was healthy over a run that had stopped
// for exactly this.
const SCOPE_REFUSAL = "Missing GoHighLevel scopes";

const scopesBlocked = computed(
  () =>
    hasMissingScopes.value ||
    (backfill.value.failure ?? "").startsWith(SCOPE_REFUSAL)
);

const connectionIssue = computed(() => {
  if (!ghl.value?.api_key_present) return null;
  if (scopesBlocked.value) {
    return {
      short: "Missing Scopes",
      dot: "ghl-status-dot--bad",
      text: "text-red-600",
      pill: "bg-red-50 text-red-700"
    };
  }
  if (duplicateOpportunitiesState.value === "disabled") {
    return {
      short: "Multiple Opportunities Off",
      dot: "ghl-status-dot--warn",
      // yellow-600 rather than Tailwind's amber/yellow-700+, which render as a muddy
      // brown at these weights. This matches the dot, so the chip reads as one colour.
      text: "text-yellow-600",
      pill: "bg-yellow-50 text-yellow-600"
    };
  }

  return null;
});

const connectionSummary = computed(() => {
  if (!ghl.value?.api_key_present) return "Required";
  if (connectionIssue.value) {
    return `Connected · Edit · ${connectionIssue.value.short}`;
  }

  return backfillRunning.value ? "Backfilling..." : "Connected · Edit";
});

// --- sync progress -------------------------------------------------------------
// Drawn before a run has counted anything, so the five steps are always visible and the
// panel never opens empty. Replaced by the real rows the moment the server has them.
const PHASE_SKELETON = [
  { key: "contacts", label: "Contacts" },
  { key: "service_addresses", label: "Service addresses" },
  { key: "opportunities", label: "Jobs to opportunities", capped: true },
  { key: "memberships", label: "Memberships" },
  { key: "appointments", label: "Appointments", capped: true }
].map((row) => ({ ...row, total: 0, done: 0, state: "waiting" }));

// Whatever the run last counted, including after it stopped: the state outlives the run
// now, so a paused sweep reopens on the bars it froze at. Only the step in flight is
// held back before the gates pass, since nothing has been pushed yet.
const backfillPhases = computed(() => {
  const rows = backfill.value.phases ?? [];
  if (!rows.length) return PHASE_SKELETON;
  if (syncStarted.value || backfill.value.state !== "running") return rows;

  return rows.map((row) =>
    row.state === "running" ? { ...row, state: "waiting" } : row
  );
});

// A run in flight reports plainly, because the number moves and a coloured chip that
// never settles reads as an alert. Every state it comes to rest in is a chip, since that
// is the one an admin scans the list for.
const syncBadge = computed(() => {
  const state = backfill.value.state;

  // Nothing in the chip while the gates are being read: the line under the heading says
  // it already, and the two together read as two separate things happening.
  if (backfillRunning.value && !syncStarted.value) {
    return { label: "", pill: null };
  }
  if (backfillRunning.value && backfill.value.steps) {
    return {
      label: `Step ${backfill.value.step} Of ${backfill.value.steps} · ${backfill.value.percent}% Overall`,
      pill: null
    };
  }
  if (state === "complete") {
    return failureCount.value
      ? { label: "Complete · With Issues", pill: "bg-green-50 text-green-700" }
      : { label: "Complete", pill: "bg-green-50 text-green-700" };
  }
  if (state === "paused") {
    return { label: backfillTitle.value, pill: "bg-yellow-50 text-yellow-600" };
  }
  if (state === "failed") {
    return { label: backfillTitle.value, pill: "bg-red-50 text-red-700" };
  }

  return { label: backfillTitle.value, pill: null };
});

function phasePercent(row) {
  if (!row.total) return row.state === "complete" ? 100 : 0;

  return Math.min(Math.round((row.done * 100) / row.total), 100);
}

const PHASE_ICONS = {
  complete: "fa-solid fa-circle-check text-green-600",
  partial: "fa-solid fa-circle-exclamation text-yellow-500",
  running: "fa-solid fa-circle-dot text-blue-600",
  stopped: "fa-solid fa-circle-pause text-gray-400",
  waiting: "fa-regular fa-circle text-gray-300"
};

// A step is only green when everything in it landed. GoHighLevel refuses records the
// sweep correctly lets go of — a contact with no email, a duplicate — and those stay
// unsynced, so a finished step can sit well below its total. A green tick over a
// one-third bar read as a contradiction; this says the step is done and some of it
// did not make it.
function phaseState(row) {
  if (row.state !== "complete") return row.state;

  return row.total && row.done < row.total ? "partial" : "complete";
}

function phaseIcon(row) {
  return PHASE_ICONS[phaseState(row)] ?? PHASE_ICONS.waiting;
}

const PHASE_BARS = {
  complete: "bg-green-500",
  partial: "bg-yellow-500",
  stopped: "bg-gray-400"
};

function phaseBar(row) {
  return PHASE_BARS[phaseState(row)] ?? "bg-blue-500";
}

// Short form of the window, for the badge on the two capped steps.
const WINDOW_BADGES = {
  "90d": "90 D",
  "6m": "6 MO",
  "1y": "1 YR"
};
// The picker is only offered before a run starts, and until it does the choice lives
// in the local ref rather than on the config. Reading the stored value throughout left
// the badge saying 90 D while the picker said 1 year, naming a cap the run would not use.
const backfillWindowBadge = computed(() => {
  const key =
    backfillControl.value?.action === "start"
      ? backfillWindow.value
      : ghl.value?.backfill_window;

  return WINDOW_BADGES[key] ?? "CAPPED";
});

const failureCount = computed(() =>
  failureGroups.value.reduce((sum, group) => sum + (group.count ?? 0), 0)
);

const enabledTagCount = computed(
  () => Object.values(tagSettings.value).filter(Boolean).length
);

const ghl = computed(() => store.state.ghl);
const tokenLocked = computed(
  () => !!ghl.value?.api_key_present && !replacingToken.value
);
// Every scope is probed, so there are only two answers (see GHL::ScopeVerifier).
const SCOPE_ICONS = {
  missing: "fa-regular fa-circle-xmark text-red-500",
  confirmed: "fa-regular fa-circle-check text-green-600"
};
const scopeIcon = (status) => SCOPE_ICONS[status] || SCOPE_ICONS.missing;

// Probed live every time the card or modal opens, never remembered: an admin who
// has just fixed their scopes in GoHighLevel must see that reflected, and one who
// has just removed them must see the warning appear.
const scopeCheck = ref(null);
const inFlightScopeCheck = ref(false);

// The sweep runs the same scope check before it pushes anything, and this window cannot
// get an answer of its own while it does — both probes compete for the same GoHighLevel
// burst allowance. It is a scope check in progress either way, so it reads as one rather
// than as a failure to reach GoHighLevel.
//
// `verifying` is the server saying a probe is genuinely outstanding: landing on the
// page clears the stored answer and asks for a fresh one, so this cannot be a stale
// snapshot left over from an earlier visit.
// A run that has counted its totals has passed BOTH gates: the scopes and the
// duplicate-opportunity setting are verified before anything is counted, and a refusal
// returns before the totals are ever written. So a total arriving is the moment
// verification ended and syncing began, which is when the spinner should come down.
//
// Read from the totals rather than from the task's progress sentence. That sentence is
// written by a separate ticker job, so a ticker that has not run yet, or has stopped,
// left the window insisting it was still checking scopes over a progress bar that was
// visibly moving.
const syncStarted = computed(
  () => backfill.value.state === "running" && backfill.value.total > 0
);

// Covers both checks, not just the scopes: the sync will not start until the
// duplicate-opportunity setting has been read either, so saying "checking" while only
// that one is outstanding is the honest state.
//
// `verifying` is the server saying a probe is genuinely outstanding — landing clears
// the stored answer and asks for a fresh one, so it cannot be a stale snapshot.
const scopesChecking = computed(
  () =>
    !syncStarted.value &&
    (inFlightScopeCheck.value ||
      scopeCheck.value?.verifying === true ||
      duplicateChecking.value)
);

// Parsed out of the run's own refusal. The sweep verifies the same thirteen scopes at
// its gate and stores the answer, but nothing re-reads it when a resume is refused, so
// the section knew it was blocked while holding a probe result from before the scopes
// were removed and listing nothing.
const refusedScopes = computed(() => {
  const failure = backfill.value.failure ?? "";
  if (!failure.startsWith(SCOPE_REFUSAL)) return [];

  return failure
    .slice(SCOPE_REFUSAL.length)
    .replace(/^:\s*/, "")
    .replace(/\.\s*$/, "")
    .split(",")
    .map((scope) => scope.trim())
    .filter(Boolean);
});

const scopeStatuses = computed(() => {
  const statuses = scopeCheck.value?.scope_statuses || [];
  if (statuses.length || !refusedScopes.value.length) return statuses;

  return requiredScopes.value.map((name) => ({
    name,
    status: refusedScopes.value.includes(name) ? "missing" : "confirmed"
  }));
});
const missingScopes = computed(() =>
  scopeCheck.value?.missing_scopes?.length
    ? scopeCheck.value.missing_scopes
    : refusedScopes.value
);
const hasMissingScopes = computed(() => missingScopes.value.length > 0);
// GoHighLevel could not be reached, so we do not know. Distinct from "nothing
// missing" — staying silent here would read as an all-clear we never established.
// GoHighLevel refused every probe: the token is rotated, revoked or deleted. Nothing
// read through it can be trusted, so this state replaces the connection panel rather
// than sitting alongside it claiming "Connected".
const tokenRejected = computed(() => !!scopeCheck.value?.token_rejected);
// GoHighLevel said outright that it does not know this token, rather than refusing
// scopes on one it does know. Different cause, different fix, so it is worded
// separately: re-selecting scopes cannot rescue a token that has been rotated away.
const tokenInvalid = computed(() => !!scopeCheck.value?.token_invalid);

// `verifying` means the sweep was running this same check when the payload was built —
// not that GoHighLevel was unreachable. Saying otherwise sends someone to fix a token
// that is fine.
const scopeCheckUnknown = computed(
  () =>
    ghl.value?.api_key_present &&
    scopeCheck.value?.checked === false &&
    scopeCheck.value?.verifying !== true
);
const requiredScopes = computed(
  () => scopeCheck.value?.required_scopes || ghl.value?.required_scopes || []
);

// A probe takes seconds, and opening, saving and disconnecting all start one. Only
// the newest may write its answer: otherwise disconnecting mid-probe lets the late
// response paint a scope warning back onto a card that no longer has a token.
let latestScopeCheck = 0;

// Nothing to probe until a token is stored, so a disconnected card makes no
// GoHighLevel round-trip.
// `refresh` re-checks with GoHighLevel rather than reading the last stored answer.
// Set when someone lands on the page or opens the window; never from the poll, which
// would be thirteen GoHighLevel calls a second.
function refreshScopeCheck({ refresh = false } = {}) {
  const seq = ++latestScopeCheck;
  if (!ghl.value?.api_key_present) {
    scopeCheck.value = null;
    inFlightScopeCheck.value = false;
    return Promise.resolve();
  }
  inFlightScopeCheck.value = true;
  const settle = (value: unknown) => {
    if (seq !== latestScopeCheck) return;
    scopeCheck.value = value;
    inFlightScopeCheck.value = false;
  };
  return (
    store
      .dispatch("checkGhlScopes", { refresh })
      .then(settle)
      // A transport failure and a `checked: false` body mean the same thing: we could
      // not find out. Both land here so the UI has one "unknown" state to render.
      .catch(() => settle({ checked: false }))
  );
}

// GoHighLevel rejects a rotated/revoked token with 401/403. Rather than keep
// showing a reassuring green "Connected", surface a reconnect prompt when the
// most recent outbound attempt failed for auth — but only when that failure is
// newer than the last time the credentials were known good. Saving re-verifies
// the token (bumping updated_at) and a successful sync bumps last_sync_at, so
// either clears a stale warning without waiting for the failures to age out.
const needsReauth = computed(() => {
  if (!ghl.value?.api_key_present) return false;
  // Asked of the server rather than dug out of the groups: grouping is ordered by
  // recency of each group, which is not the same as the single newest rejection.
  const newest = ghl.value?.newest_outbound_failure;
  if (!newest || ![401, 403].includes(newest.response_code)) return false;
  const knownGoodAt = Math.max(
    Date.parse(ghl.value?.updated_at ?? "") || 0,
    Date.parse(ghl.value?.last_sync_at ?? "") || 0
  );
  return (Date.parse(newest.created_at) || 0) > knownGoodAt;
});

// Which groups and which individual failures are open. Keyed rather than a flag on the
// row, so a refetch while a run is going does not collapse what someone is reading.
const openGroups = ref({});

// Short on purpose. The picker sits inline beside the section heading, and the full
// wording ("Last 24 hours") pushed it onto its own line inside the modal.
const WINDOW_LABELS = {
  "1h": "1h",
  "24h": "24h",
  "7d": "7d"
};

// The same three periods written out, for the sentences that read them rather than the
// picker that labels them.
const WINDOW_PROSE = {
  "1h": "last hour",
  "24h": "last 24 hours",
  "7d": "last 7 days"
};

function windowProse(key) {
  return WINDOW_PROSE[key] ?? key;
}

const failureWindow = ref("24h");

const failureWindows = computed(
  () => ghl.value?.outbound_failure_windows ?? ["1h", "24h", "7d"]
);

const failureGroups = computed(() => ghl.value?.outbound_failures ?? []);

function windowLabel(key) {
  return WINDOW_LABELS[key] ?? key;
}

// Three choices shown side by side rather than hidden behind a select: which period you
// are looking at changes what every count below means, so it should be readable without
// opening anything.
const windowButtons = computed(() =>
  failureWindows.value.map((key) => ({
    label: windowLabel(key),
    selected: key === failureWindow.value
  }))
);

// The connect form's jobs-and-appointments cap. Same control as the failure-window
// picker so the two read as one kind of choice.
const backfillWindowButtons = computed(() =>
  BACKFILL_WINDOWS.map((option) => ({
    label: option.label,
    selected: option.key === backfillWindow.value
  }))
);

function pickBackfillWindow(buttons) {
  const index = buttons.findIndex((button) => button.selected);
  const option = BACKFILL_WINDOWS[index];
  if (option) backfillWindow.value = option.key;
}

function pickWindow(buttons) {
  const index = buttons.findIndex((button) => button.selected);
  const key = failureWindows.value[index];
  if (!key || key === failureWindow.value) return;

  failureWindow.value = key;
  reloadFailures();
}

// Ten rows to start, five more per click. Held per group and reset whenever the window
// closes, so reopening starts from ten again rather than from wherever the last visit
// left off. The list scrolls inside a fixed height, so revealing more never moves the
// sections below it.
const PAGE_FIRST = 10;
const PAGE_MORE = 5;
const shownPerGroup = ref<Record<string, number>>({});

function shownFailures(group) {
  return (group.failures ?? []).slice(
    0,
    shownPerGroup.value[group.key] ?? PAGE_FIRST
  );
}

// What another click can actually reveal. The true count runs to thousands while the
// sample is capped, so offering "+ 8,990 more" on rows that are not there would be a
// promise the window cannot keep.
function hiddenCount(group) {
  const held = (group.failures ?? []).length;

  return Math.max(held - (shownPerGroup.value[group.key] ?? PAGE_FIRST), 0);
}

// One list element per group, so a reveal can nudge the right one.
const rowLists = {};

function revealMore(group) {
  const shown = shownPerGroup.value[group.key] ?? PAGE_FIRST;
  shownPerGroup.value = {
    ...shownPerGroup.value,
    [group.key]: shown + PAGE_MORE
  };

  // The list is a fixed height, so revealing rows changes nothing an eye can catch: the
  // new ones are below the fold and the box looks identical. Nudging it down puts the
  // first of them in view and is what tells you the rest are there to scroll to.
  nextTick(() => {
    const list = rowLists[group.key];
    if (!list) return;

    list.scrollTo({
      top: Math.min(
        list.scrollTop + list.clientHeight * 0.6,
        list.scrollHeight
      ),
      behavior: "smooth"
    });
  });
}

// A rate limit clears itself, so it is the one group that is not worth alarming about.
function groupBadgeClass(group) {
  return group.response_code === 429
    ? "bg-yellow-50 text-yellow-700"
    : "bg-red-50 text-red-700";
}

// What kind of thing the row is about, in Sera's words. Derived from the operation
// rather than the endpoint, and mapped rather than de-pluralised: stripping the trailing
// s turned "opportunities" into "opportunitie", and the raw prefix is lower case.
const RECORD_LABELS = {
  contacts: "Contact",
  opportunities: "Job",
  "objects/record": "Record",
  "objects/schema": "Object",
  locations: "Location",
  "custom-fields": "Field",
  associations: "Link"
};

// Whether to reserve a name column. A group where GoHighLevel named nobody (missing
// contact details, say) reads better with the ids against the right edge than with an
// empty column held open beside them.
function groupHasNames(group) {
  return (group.failures ?? []).some((failure) => failure.contact_name);
}

function recordLabel(failure) {
  const prefix = failure.operation?.split(".")[0] ?? "";

  return RECORD_LABELS[prefix] ?? "Record";
}

function toggleGroup(key) {
  openGroups.value = { ...openGroups.value, [key]: !openGroups.value[key] };
}

// Counting over a different period is a different question, so it goes back to the
// server rather than being filtered client-side from whatever the last fetch held.
//
// Every other fetch sends it too. The payload carries the failures for whichever window
// it was asked for, so a refresh that left this out — the five-second poll during a
// sweep, most visibly — answered with the default period while the picker still showed
// the one that had been chosen.
function failureParams() {
  return { failureWindow: failureWindow.value };
}

function reloadFailures() {
  return store.dispatch("fetchGhl", failureParams());
}

const lastSyncLabel = computed(() =>
  ghl.value?.last_sync_at ? formatTimestamp(ghl.value.last_sync_at) : "Never"
);

// GHL's "Allow more than one opportunity per contact" location setting. It is read
// on demand (modal open / re-check) via a dedicated endpoint, NOT as part of the
// health payload — so a Marketplace page load never blocks on a GHL round-trip.
// undefined = not read yet, null = read failed / not connected → both "unknown".
const duplicateOpportunitiesAllowed = ref<boolean | null | undefined>(
  undefined
);
const duplicateChecking = ref(false);

const duplicateOpportunitiesState = computed(() => {
  const allowed = duplicateOpportunitiesAllowed.value;
  if (allowed === true) return "enabled";
  if (allowed === false) return "disabled";
  return "unknown";
});

const duplicateStatusLabel = computed(() => {
  if (!ghl.value?.api_key_present) return "Set this up in GoHighLevel first";

  return {
    enabled: "Enabled",
    disabled: "Not enabled",
    unknown: "Not checked"
  }[duplicateOpportunitiesState.value];
});

// Three states, not two. An unread setting shared the amber dot and the "turn this on"
// instructions with a setting genuinely off, so a tenant whose daily quota had run out
// was told to go and change something in GoHighLevel that was already correct.
const duplicateDotClass = computed(
  () =>
    ({
      enabled: "ghl-status-dot",
      disabled: "ghl-status-dot--warn",
      unknown: "ghl-status-dot--unknown"
    })[duplicateOpportunitiesState.value]
);

const duplicateTextClass = computed(() =>
  duplicateOpportunitiesState.value === "disabled"
    ? "text-amber-700"
    : "text-gray-500"
);

// Card-face warning, shown only when GHL is connected and the setting is
// confirmed off. An unknown/unread state stays silent rather than nagging.
// Blank while a probe is running, so a result left over from the last one cannot sit
// on the card contradicting an answer that is about to replace it.
// The toast shown when someone switches GoHighLevel on before saving credentials. A
// toast only: the card has its own messages and the modal opening unasked took over the
// screen for what is a one-line correction.
const SETUP_REQUIRED =
  "Add your Private Integration Token and Location ID in Set Up Information before enabling GoHighLevel.";

const cardError = computed(() => {
  // Nothing is syncing while it is switched off, so a missing scope is not a problem
  // the card needs to raise. It is still reported inside the window.
  if (!ghl.value?.enabled || scopesChecking.value) return "";

  return scopeCheck.value?.missing_scopes_message;
});

// One line for the card: the duplicate-opportunity setting, else the sweep. The scope
// probe says nothing here — it runs on every page load and its spinner turned a card
// that was working perfectly into one that looked permanently busy. The modal still
// shows it, which is where someone has actually asked about their token.
const cardNotice = computed(() => {
  if (duplicateNotice.value) return duplicateNotice.value;

  // Last, because a missing scope or the duplicate setting both stop the sync
  // outright and are the more urgent thing to say.
  return backfillNotice.value;
});

// Blank progress means the run has not pushed anything yet, and the first thing it
// does is verify the scopes and the duplicate-opportunity setting — fourteen GoHighLevel
// round trips before a single record moves. Saying "Starting..." for that stretch reads
// as a stall; naming the check says what is happening and that syncing follows it.
const PENDING_CHECKS = "Checking scopes, sync starts once they pass";

const backfillNotice = computed(() => {
  const { state, progress } = backfill.value;
  if (state === "running") {
    return (
      progress ||
      (syncStarted.value ? "Syncing existing records" : PENDING_CHECKS)
    );
  }
  // Two ways to be paused, and only one of them ends by itself. Telling an admin who
  // pressed Pause that it resumes automatically is the opposite of what happens.
  if (state === "paused") {
    return backfill.value.resumable
      ? "Sync paused"
      : "Sync paused, resumes automatically";
  }

  return "";
});

// Defaults to the "never started" shape so the template can read it before the
// first fetchGhl resolves.
//
// Gated on the integration being switched on, not merely on a token being stored.
//
// The sweep's state lives on the background task rather than the config row, and that
// is deliberate: the task is the run-once marker and has to outlive a disconnect, or
// reconnecting would re-push the tenant's whole history. The side effect is that a
// tenant who has switched GoHighLevel off still has a backfill state to report, and
// nothing about a stopped integration is worth a warning on the card — least of all
// "Sync paused, resumes automatically", when nothing is going to resume.
const backfill = computed(() =>
  ghl.value?.api_key_present && ghl.value?.enabled
    ? (ghl.value?.backfill ?? { state: "none" })
    : { state: "none" }
);

const BACKFILL_TITLES: Record<string, string> = {
  none: "Not Synced",
  running: "Syncing",
  paused: "Paused",
  failed: "Stopped",
  complete: "Synced"
};

const backfillTitle = computed(
  () => BACKFILL_TITLES[backfill.value.state] ?? ""
);

// While running, progress is the live per-phase line the job writes; once it has
// stopped, the summary counts what actually synced. A paused sweep shows its reason.
const backfillDetail = computed(() => {
  const { state, summary, failure } = backfill.value;
  if (state === "none") {
    return "Your existing customers, jobs, memberships and appointments have not been sent to GoHighLevel yet.";
  }
  // Deliberately not the task's progress sentence. That sentence carries its own
  // counts, written by a ticker every few seconds, and the bar below carries live ones,
  // so the modal showed the same thing twice with two different numbers. The phase is
  // the only part the bar does not already say, so only the phase is shown.
  if (state === "running") {
    return syncStarted.value ? phaseLabel.value : `${PENDING_CHECKS}.`;
  }
  if (state === "paused") return failure || "";
  if (state === "failed") return failure || "";
  return summary || "";
});

// "Contacts (step 1 of 5)", from the live payload rather than the ticker's sentence.
const phaseLabel = computed(() => {
  const { phase_label: label, step, steps } = backfill.value;
  if (!label) return "";

  return step && steps ? `${label} (step ${step} of ${steps})` : label;
});

const backfillBusy = ref(false);

// Pausing and resuming are two different requests, retrying and resuming are the same
// one. What separates them here is only what the admin is told, because "your sync
// broke, try again" and "you stopped it, carry on" are not the same sentence.
const BACKFILL_CONTROLS = {
  start: {
    action: "start",
    starts: true,
    label: "Sync now",
    busy: "Starting...",
    icon: "fa-regular fa-circle-play",
    title: "Send your existing records to GoHighLevel",
    dispatch: "retryGhlBackfill",
    done: "Sync started.",
    failed: "Could not start the sync. Try again shortly."
  },
  pause: {
    action: "pause",
    label: "Pause",
    busy: "Pausing...",
    icon: "fa-regular fa-circle-pause",
    title: "Stop the sync where it is",
    dispatch: "pauseGhlBackfill",
    done: "Sync paused.",
    failed: "Could not pause the sync. Try again shortly."
  },
  resume: {
    action: "resume",
    starts: true,
    label: "Resume",
    busy: "Resuming...",
    icon: "fa-regular fa-circle-play",
    title: "Carry on from where the sync stopped",
    dispatch: "retryGhlBackfill",
    done: "Sync resumed.",
    failed: "Could not resume the sync. Try again shortly."
  },
  retry: {
    action: "retry",
    starts: true,
    label: "Retry",
    busy: "Retrying...",
    icon: "fa-regular fa-arrows-rotate",
    title: "Start the sync again",
    dispatch: "retryGhlBackfill",
    done: "Sync restarted.",
    failed: "Could not restart the sync. Try again shortly."
  }
};

const backfillControl = computed(() => {
  const { startable, pausable, resumable, retryable } = backfill.value;
  if (startable) return BACKFILL_CONTROLS.start;
  if (pausable) return BACKFILL_CONTROLS.pause;
  if (resumable) return BACKFILL_CONTROLS.resume;

  return retryable ? BACKFILL_CONTROLS.retry : null;
});

// None of these re-save the credentials. Saving used to be the only way to ask for a
// run, which reads as a reconnect and re-verifies a token that was never the problem.
// The server decides whether a run is warranted, so a repeated click cannot stack runs.
async function runBackfillControl() {
  const control = backfillControl.value;
  if (!control) return;

  backfillBusy.value = true;
  try {
    await store.dispatch(
      control.dispatch,
      control.starts ? backfillWindow.value : undefined
    );
    window.notify.success(control.done);
    trackRunningSweep();
    // The run re-verifies the scopes at its gate and stores what it finds. Reading it
    // back here is what keeps the section's list current after a resume.
    refreshScopeCheck().then(trackScopeCheck);
  } catch (error) {
    // The server's sentence when it gave one. It refuses a run the gate would only
    // halt a moment later, and "try again shortly" is the wrong advice for that.
    window.notify.error(error?.response?.data?.message || control.failed);
  }
  backfillBusy.value = false;
}

// The sweep's own task is broadcast on every write (Admins::BackgroundTask), and the
// notification centre already holds that subscription. Following its copy in the store
// is what makes this window live without a second subscription of its own — the poll
// below stays as the fallback for a page where the bell is not mounted.
const GHL_BACKFILL_TASK = "GoHighLevel backfill";

const liveBackfillTask = computed(() =>
  (store.state.backgroundTasks || []).find(
    (task) => task.description === GHL_BACKFILL_TASK
  )
);

// Nothing else re-reads the probe once a sweep is under way, so a token that has just
// been given its missing scopes went on being reported as short of them: the sweep
// cleared its own gate and stored a clean answer that the window never asked for.
//
// Watched in both directions. Starting means the gate passed, so the stale failure is
// gone; a fresh refusal means the gate stored a new answer worth reading.
watch([syncStarted, () => backfill.value.failure], () => {
  if (!modalOpen.value || !ghl.value?.api_key_present) return;

  refreshScopeCheck();
  // The other half of the same gate, so a run refused for this is reported too.
  refreshDuplicateSetting();
});

// Both gate refusals are fixed in Connection, so the section opens itself the moment one
// appears. Watched rather than decided once on open, because a sweep can be refused
// minutes after the window was opened.
watch(connectionIssue, (issue) => {
  if (issue && modalOpen.value) connectionCollapsed.value = false;
});

watch(
  () => liveBackfillTask.value?.updatedAt,
  (updatedAt, previous) => {
    if (!updatedAt || updatedAt === previous) return;
    // The task carries the progress line; the counts behind the bar come from the
    // settings payload, so this re-reads rather than copying what the bell has.
    store.dispatch("fetchGhl", failureParams());
  }
);

const duplicateNotice = computed(() =>
  ghl.value?.enabled && duplicateOpportunitiesState.value === "disabled"
    ? "Multiple opportunities off"
    : ""
);

// Sequenced like the scope probe, and for the same reason: this is a GoHighLevel
// round-trip, and reopening or re-checking starts another before the first answers.
// Only the newest may write, or a stale read reports a setting already contradicted.
let latestDuplicateRead = 0;

function refreshDuplicateSetting() {
  const seq = ++latestDuplicateRead;
  if (!ghl.value?.api_key_present) {
    duplicateOpportunitiesAllowed.value = undefined;
    duplicateChecking.value = false;
    return Promise.resolve();
  }
  duplicateChecking.value = true;
  const settle = (allowed: boolean | null) => {
    if (seq !== latestDuplicateRead) return;
    // A failed read does not overwrite an answer GoHighLevel already gave. The
    // setting only changes when someone changes it in GoHighLevel, so throwing away
    // a confirmed one because the daily limit is spent reports a working tenant as
    // unknown for the hours a paused sync waits.
    if (allowed !== null || duplicateOpportunitiesAllowed.value === undefined) {
      duplicateOpportunitiesAllowed.value = allowed;
    }
    duplicateChecking.value = false;
  };
  return store
    .dispatch("checkGhlDuplicateOpportunities")
    .then(settle)
    .catch(() => settle(null));
}

function formatTimestamp(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}

watch(switchOn, (newValue, oldValue) => {
  if (
    oldValue === null ||
    newValue === oldValue ||
    newValue === ghl.value?.enabled
  ) {
    return;
  }
  if (newValue) {
    enableIntegration();
  } else {
    disableIntegration();
  }
});

watch(
  () => ghl.value?.enabled,
  (newValue, oldValue) => {
    if (oldValue !== newValue) refreshTools();
  }
);

// A sweep finishes asynchronously and nothing pushes that to an open window, so every
// value here — progress, last sync, and the scope answer — is a snapshot from whenever
// it was last fetched. Left alone the window showed "checking scopes" over a sync that
// had already finished, which is how this looked broken while the sync was fine.
//
// Only while a sweep is actually in flight, and it stops itself the moment one is not:
// no polling on an idle card.
const SWEEP_POLL_MS = 5000;
let sweepPoll: ReturnType<typeof setInterval> | null = null;

function stopTrackingSweep() {
  if (!sweepPoll) return;

  clearInterval(sweepPoll);
  sweepPoll = null;
}

function sweepInFlight() {
  return backfill.value.state === "running";
}

// The scope probe is followed separately, and faster. It answers in a second or two,
// while the sweep poll is five seconds and re-reads the whole config before it gets to
// the scope check — so the window sat on "Checking scopes" long after the answer had
// landed. This asks for the one thing it is waiting on.
const SCOPE_POLL_MS = 1000;
// Matches ScopeCheckJob::DEADLINE, which is also what bounds `verifying` on the server.
// A probe that never answers stops the poll here rather than leaving it running for the
// life of the page.
const SCOPE_POLL_LIMIT = 120;
let scopePoll: ReturnType<typeof setInterval> | null = null;
let scopePolls = 0;

function stopTrackingScopeCheck() {
  if (!scopePoll) return;

  clearInterval(scopePoll);
  scopePoll = null;
}

function trackScopeCheck() {
  if (scopePoll || scopeCheck.value?.verifying !== true) return;

  scopePolls = 0;
  scopePoll = setInterval(() => {
    scopePolls += 1;
    if (scopeCheck.value?.verifying !== true || scopePolls > SCOPE_POLL_LIMIT) {
      return stopTrackingScopeCheck();
    }

    refreshScopeCheck();
  }, SCOPE_POLL_MS);
}

function trackRunningSweep() {
  if (sweepPoll || !sweepInFlight()) return;

  sweepPoll = setInterval(() => {
    if (!sweepInFlight()) return stopTrackingSweep();

    store.dispatch("fetchGhl", failureParams()).then(() => {
      if (!sweepInFlight()) stopTrackingSweep();
    });
  }, SWEEP_POLL_MS);
}

// Rejections land whenever GoHighLevel refuses a push, which is not tied to a sweep: a
// live callback pushing one customer can fail while nothing else is running. The sweep
// poll only follows a sweep, so outside one the list was whatever it was when the window
// opened, and someone watching it saw nothing arrive.
//
// Only while the window is open, and only for a connected tenant: a Marketplace page
// with the modal shut has nothing to refresh and must not poll.
const FAILURES_POLL_MS = 5000;
let failuresPoll: ReturnType<typeof setInterval> | null = null;

function stopTrackingFailures() {
  if (!failuresPoll) return;

  clearInterval(failuresPoll);
  failuresPoll = null;
}

function trackFailures() {
  if (failuresPoll || !modalOpen.value || !ghl.value?.api_key_present) return;

  failuresPoll = setInterval(() => {
    // The sweep poll already re-reads the whole payload on a faster path; running both
    // would double the requests for the same answer.
    if (!modalOpen.value) return stopTrackingFailures();
    if (sweepPoll) return;

    reloadFailures();
  }, FAILURES_POLL_MS);
}

onUnmounted(() => {
  stopTrackingSweep();
  stopTrackingScopeCheck();
  stopTrackingFailures();
});

onMounted(() => {
  store.dispatch("fetchGhl", failureParams()).then(() => {
    refreshTools();
    // Only connected tenants read these; both no-op otherwise, so a non-connected
    // card makes no GoHighLevel round-trip.
    refreshDuplicateSetting();
    refreshScopeCheck({ refresh: true }).then(trackScopeCheck);
    trackRunningSweep();
  });
});

function openModal() {
  store.dispatch("fetchGhl", failureParams()).then(() => {
    syncFormFromConfig();
    modalOpen.value = true;
    trackFailures();
    refreshDuplicateSetting();
    // No scope request at all. Landing on the page already probed and the answer is in
    // state; asking again — even for the stored value — is a round trip for a window
    // opened seconds later. The poll below keeps it current if one is still running.
    trackScopeCheck();
    trackRunningSweep();
  });
}

// Reads the form back out of the stored config. The token input is deliberately
// cleared rather than repopulated: the server only ever reports whether a key is
// present, never the key itself, so anything shown there would be a placeholder
// the admin could mistake for the real token.
function syncFormFromConfig() {
  apiKey.value = "";
  // Re-locked on every open, so the field is never left editable from a previous
  // visit where the admin selected the box and then changed their mind.
  replacingToken.value = false;
  runBackfill.value = true;
  backfillWindow.value = ghl.value?.backfill_window ?? DEFAULT_BACKFILL_WINDOW;
  realtimePush.value = ghl.value?.realtime_push !== false;
  locationId.value = ghl.value?.location_id ?? "";
  tagSettings.value = resolvedTagSettings();
}

// The serializer resolves "absent means on" server-side, but fall back to on
// here too so an older payload cannot render every axis as unchecked.
function resolvedTagSettings(): Record<string, boolean> {
  const saved = ghl.value?.tag_settings ?? {};
  return Object.fromEntries(
    TAG_AXES.map((axis) => [axis.key, saved[axis.key] !== false])
  );
}

// Modal renders its delete button only when something is listening for the event, so
// binding the handler conditionally is what hides it. There is nothing to disconnect
// before a token is stored, and offering it then invites a confusing no-op.
const disconnectListener = computed(() =>
  ghl.value?.api_key_present ? { delete: confirmDelete } : {}
);

function closeModal() {
  stopTrackingFailures();
  helpTopic.value = null;
  // Paging is per visit. Reopening a group that was expanded to fifty rows last time
  // should start at ten again.
  shownPerGroup.value = {};

  return store.dispatch("fetchGhl", failureParams()).then(() => {
    modalOpen.value = false;
    refreshTools();
  });
}

function enableIntegration() {
  // The toggle stays live without a token so the refusal can say why, rather than the
  // control simply not responding. It bounces back off, states the reason on the card
  // itself, and opens the place the credentials are entered.
  if (!ghl.value?.api_key_present) {
    // Reverted on the next tick, not this one. Flipping it back synchronously leaves
    // the value unchanged across the render, so Vue patches nothing and the browser's
    // own checkbox stays visibly on — the integration reading as enabled when the
    // server was never asked to enable anything.
    nextTick(() => {
      switchOn.value = false;
    });
    window.notify.error(SETUP_REQUIRED);
    return;
  }
  store
    .dispatch("changeGhl", { enabled: true })
    .then(() => window.notify.success("GoHighLevel integration enabled!"))
    .catch(() => (switchOn.value = false));
}

function disableIntegration() {
  // Nothing is stored (e.g. just disconnected) — there is nothing to disable,
  // and posting enabled:false with no token trips the api_key validation.
  if (!ghl.value?.api_key_present) return;
  store
    .dispatch("changeGhl", { enabled: false })
    .then(() => window.notify.success("GoHighLevel integration disabled!"));
}

async function saveIntegration() {
  if (!ghl.value?.api_key_present && !apiKey.value) {
    window.notify.error("A Private Integration Token is required.");
    return;
  }
  if (!locationId.value.trim()) {
    window.notify.error("A Location ID is required.");
    return;
  }
  try {
    saving.value = true;
    const payload: Record<string, unknown> = {
      location_id: locationId.value,
      enabled: true
    };
    if (apiKey.value) payload.api_key = apiKey.value;
    // Sent on the first connect too, now the axes are settable before one: they
    // decide what that very first push carries, so leaving them out would sweep
    // the whole tenant under defaults the admin had just changed.
    payload.tag_settings = { ...tagSettings.value };
    if (!ghl.value?.api_key_present) {
      // Only on the first connect, which is the only save that starts a sweep.
      payload.run_backfill = runBackfill.value;
      // Only when a sweep is actually being asked for. Selecting the box, picking three
      // months and then deselecting it would otherwise store a window for a run nobody
      // started; the picker beside Sync now is where that choice is made instead.
      if (runBackfill.value) payload.backfill_window = backfillWindow.value;
    }
    const connecting = !ghl.value?.api_key_present;
    const sweepRequested = connecting && runBackfill.value;
    await store.dispatch("changeGhl", payload);
    window.notify.success("GoHighLevel settings saved!");
    // Stay open and re-read instead of closing. Saving a token is the step whose
    // result the admin needs to see — the scope list, and the tag axes that only
    // render once a key is stored — and closing would hide all of it behind
    // reopening the modal.
    await store.dispatch("fetchGhl", failureParams());
    syncFormFromConfig();
    // Land on what is actually happening. A sweep was asked for, so the progress list
    // is the interesting thing and the credentials are settled; without a sweep there
    // is nothing to watch and the form stays where it was.
    if (sweepRequested) {
      connectionCollapsed.value = true;
      syncCollapsed.value = false;
    }
    refreshTools();
    refreshDuplicateSetting();
    // The token may have changed, so this is the one place a re-probe is earned. The
    // answer is a second or two behind the save, so start following it rather than
    // leaving the window on the "checking" snapshot this fetch returns.
    refreshScopeCheck({ refresh: true }).then(trackScopeCheck);
    // Saving is what starts the sweep, so this is where the card has to begin
    // following it. Without this the poll never started for the run the save itself
    // triggered: the window opened before any sweep existed, declined to poll, and
    // then sat on the one answer it had fetched — "verifying" — for good.
    trackRunningSweep();
  } catch (e: any) {
    // The backend verifies the token/location against GHL and rejects bad ones.
    window.notify.error(
      e?.response?.data?.message ||
        "Could not connect to GoHighLevel. Check the token and location id."
    );
  }
  saving.value = false;
}

function dropIntegration() {
  deleteConfirm.value = false;
  // Drop first, then sync the toggle from the fresh (disabled) config. Setting
  // switchOn manually before the drop would fire the enabled-watch and post a
  // stray change against the now-empty config.
  store.dispatch("dropGhl").then(() => {
    modalOpen.value = false;
    refreshTools();
    // There is no token left to probe, so this clears the warning rather than
    // leaving the card reporting scopes for an integration that is gone.
    refreshScopeCheck();
    window.notify.success("GoHighLevel disconnected.");
  });
}

function refreshTools() {
  switchOn.value = ghl.value?.enabled ?? false;
}

function confirmDelete() {
  deleteConfirm.value = true;
}

function toggleDeleteConfirm() {
  deleteConfirm.value = !deleteConfirm.value;
}

defineExpose({ openModal });
</script>

<style lang="scss" scoped>
.ghl-status-dot,
.ghl-status-dot--warn,
.ghl-status-dot--unknown {
  width: 8px;
  height: 8px;
  border-radius: 9999px;
  display: inline-block;
}

.ghl-status-dot--bad,
.ghl-status-dot--busy {
  width: 8px;
  height: 8px;
  border-radius: 9999px;
  display: inline-block;
}

.ghl-status-dot--unknown {
  background-color: #9ca3af;
}

.ghl-status-dot--bad {
  background-color: #dc2626;
}

.ghl-status-dot--busy {
  background-color: #3b82f6;
}

.ghl-status-dot {
  background-color: #22c55e;
}

.ghl-status-dot--warn {
  background-color: #eab308;
}
</style>

<style lang="scss">
// ButtonGroup is sized for page-level toolbars. Inside the settings modal it sits beside
// a section heading, so it is scaled down rather than restyled.
.ghl-window-picker button {
  padding: 0.2rem 0.55rem;
  font-size: 0.7rem;
  line-height: 1rem;
}

// Markers are drawn by the component, not by list-style. The modal teleports its
// content to <body> (see Modal.vue's SafeTeleport), and Tailwind's preflight resets
// ol/ul to list-style:none, so the semantic markers were being lost in the move.
// ::before content survives both, and keeps the markup a real list for a11y.
.ghl-guide-list {
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    display: block;
    position: relative;
    padding-left: 1.25rem;

    &::before {
      position: absolute;
      left: 0.25rem;
      color: #6b7280;
    }
  }
}

.ghl-guide-list--bullet li::before {
  content: "\2022"; // bullet
}

// Smaller than the default segmented control: this is one choice on a form, not the
// form's subject. Tailwind runs with important: true, so the component's own px-4/py-2
// /text-sm can only be beaten by !important.
.ghl-backfill-window-picker button {
  padding: 0.25rem 0.625rem !important;
  font-size: 0.75rem !important;
  line-height: 1.1rem !important;
}

// The toggle leads the row rather than trailing it, so the section reads as an
// accordion rather than as a heading with a button parked beside it.
//
// This block is NOT scoped, so no :deep() here. Vue only rewrites that inside a
// scoped block; elsewhere it reaches the browser verbatim and the rule is dropped.
// The wrapper class is this card's own, so the reach is the same either way.
// The guide panels are reference text, read once and closed. 13px over the modal's
// 14px keeps a five-step list and thirteen scope names on one screen, and sets them
// apart from the form behind.
.ghl-guide-body {
  font-size: 0.8125rem;
  line-height: 1.5;

  li + li {
    margin-top: 0.25rem;
  }
}

// A fixed height with its own scroll. The section must not grow as rows are revealed,
// or every section under it moves down the modal on each click.
.ghl-issue-rows {
  margin: 0;
  padding: 0;
  list-style: none;
  max-height: 13rem;
  overflow-y: auto;

  li + li {
    border-top: 1px dashed #e5e7eb;
  }
}

// min-width: 0 on the two that can overflow, because a grid item's automatic minimum
// size is its content's: without it a long name widens the column instead of truncating.
// A fixed share for the name rather than auto, so the ids line up in a column down the
// list instead of each ending wherever its neighbour's name begins.
.ghl-issue-row,
.ghl-issue-row--bare {
  display: grid;
  gap: 0.75rem;
  align-items: center;

  > :nth-child(2),
  > :nth-child(3) {
    min-width: 0;
  }
}

.ghl-issue-row {
  grid-template-columns: auto 1fr 40%;
}

.ghl-issue-row--bare {
  grid-template-columns: auto 1fr;
}

// Hairlines between the steps rather than a card each: five bordered boxes read as five
// separate things, where this is one run being reported step by step.
.ghl-phase-list {
  margin: 0;
  padding: 0;
  list-style: none;

  li + li {
    border-top: 1px solid #f3f4f6;
  }
}

// Alert ships info at h-4 and warning at h-6, and centres them against the whole
// block, so two stacked callouts show different-sized badges at different heights.
// One size, pinned to the first line. Tailwind runs with important: true here.
// `div.` rather than `.ghl-callout`: Alert carries Tailwind's items-center, which is
// also !important and equally specific, so a bare class selector only wins on source
// order. align-self on the icon settles it outright, since nothing else sets that.
//
// Both viewBoxes are square (12x12 and 13x13), so one box size distorts neither.
div.ghl-callout {
  align-items: flex-start !important;

  > svg {
    align-self: flex-start !important;
    height: 1.125rem !important;
    width: 1.125rem !important;
    margin-top: 0.0625rem;
    flex-shrink: 0;
  }
}

// Flush to the edge and full height, like the Notification Center panel, rather than
// the Drawer's inset card. Tailwind runs with important: true here, so the component's
// own margin/radius utilities can only be beaten by !important at higher specificity.
.ghl-help-drawer .drawer.grid {
  margin: 0 !important;
  max-height: 100dvh !important;
  border-radius: 0 !important;
}

// The drawer guides are ordered steps, and list-style is off on the shared list.
.ghl-guide-list--number {
  counter-reset: ghl-step;

  li {
    counter-increment: ghl-step;
    padding-left: 1.5rem;

    &::before {
      content: counter(ghl-step) ".";
      left: 0;
    }
  }
}

// Quiet uppercase labels so the three parts of the guide read as sections rather
// than one continuous wall of text.
.ghl-guide-heading {
  margin: 0 0 0.375rem;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6b7280;
}

// Thirteen scope names stacked one per line dominated the modal. They are
// identifiers, not prose, so they read fine two-up and take half the height.
.ghl-scope-grid {
  display: grid;
  grid-template-columns: 1fr;
  /* li stays display:block inside a grid cell, so ::before still anchors left. */
  column-gap: 1rem;
  row-gap: 0.125rem;
}

@media (min-width: theme("screens.sm")) {
  .ghl-scope-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
